import React from 'react'
import styled from '@emotion/styled'
import type { KambiEvent, KambiLiveData } from '@mobi/api-types/kambi'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { TeamIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon/Components/TeamIcon'
import { getEventDetailsFromEvent } from '@mobi/sports-offering/helpers/getEventDetailsFromEvent'

/** Display used for Tennis, Table Tennis and Volleyball */
export const ScoreTableDisplay: React.FC<{
  event: KambiEvent
  score?: KambiLiveData['score']
  statistics?: KambiLiveData['statistics']
  columns: { key: keyof PlayerSummary; label: string }[]
  shouldUseDarkTheme?: boolean
}> = ({ score, statistics, event, columns, shouldUseDarkTheme }) => {
  const { leftTeam, rightTeam } = getEventDetailsFromEvent(event)

  const scoreSummary = getTableScoreSummary(event.sport, statistics, {
    home: score?.home || 0,
    away: score?.away || 0,
  })

  const isHomeServing = statistics?.sets?.homeServe === true

  return (
    <WrapperStyled data-dark-theme={shouldUseDarkTheme ? 'true' : undefined}>
      <div>
        <div>
          {scoreSummary?.currentSetIndex ? displaySetNumber(scoreSummary.currentSetIndex) : '-'}
        </div>
        <div>
          {columns.map(col => (
            <span key={String(col.key)}>{col.label}</span>
          ))}
        </div>

        <div className='score-table__divider-wrapper'>
          <div />
        </div>

        <div>
          <TeamIcon
            sportName={event.sport}
            size='sm'
            participantId={leftTeam.participantId}
            defaultIconType='team-icon'
            eventId={event.id}
          />

          <div>
            <span>{leftTeam.name}</span>
            {isHomeServing && <span />}
          </div>
        </div>

        <div>
          {columns.map(col =>
            col.key === 'points' ? (
              <div key={String(col.key)}>
                <span>{scoreSummary?.home[col.key] ?? '-'}</span>
              </div>
            ) : (
              <span key={String(col.key)}>{scoreSummary?.home[col.key] ?? '-'}</span>
            )
          )}
        </div>

        <div>
          <TeamIcon
            sportName={event.sport}
            size='sm'
            participantId={rightTeam.participantId}
            defaultIconType='team-icon'
            eventId={event.id}
          />

          <div>
            <span>{rightTeam.name}</span>
            {!isHomeServing && <span />}
          </div>
        </div>

        <div>
          {columns.map(col =>
            col.key === 'points' ? (
              <div key={String(col.key)}>
                <span>{scoreSummary?.away[col.key] ?? '-'}</span>
              </div>
            ) : (
              <span key={String(col.key)}>{scoreSummary?.away[col.key] ?? '-'}</span>
            )
          )}
        </div>
      </div>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const getTableScoreSummary = (
  sport: KambiEvent['sport'],
  stats: KambiLiveData['statistics'] | undefined,
  points?: { home: number | string; away: number | string }
): TableScoreSummary | undefined => {
  if (!stats) return undefined

  const setsHome = stats.sets.home
  const setsAway = stats.sets.away

  const firstNonStartedSetIndex = setsHome.findIndex(score => score === -1)

  const lastStartedSetIndex =
    firstNonStartedSetIndex === -1 ? setsHome.length - 1 : firstNonStartedSetIndex - 1

  const currentSetArrayIndex = lastStartedSetIndex < 0 ? 0 : lastStartedSetIndex

  const setScoresToCount =
    sport === 'TABLE_TENNIS' ? currentSetArrayIndex + 1 : currentSetArrayIndex

  let homeSetsWon = 0
  let awaySetsWon = 0

  for (let i = 0; i < setScoresToCount; i++) {
    if (setsHome[i] > setsAway[i]) homeSetsWon++
    else if (setsAway[i] > setsHome[i]) awaySetsWon++
  }

  const homeGamesValue = setsHome[currentSetArrayIndex] ?? 0
  const awayGamesValue = setsAway[currentSetArrayIndex] ?? 0

  const homeGames = homeGamesValue === -1 ? 0 : homeGamesValue
  const awayGames = awayGamesValue === -1 ? 0 : awayGamesValue

  return {
    home: {
      sets: homeSetsWon,
      games: homeGames,
      points: points?.home ?? '-',
    },
    away: {
      sets: awaySetsWon,
      games: awayGames,
      points: points?.away ?? '-',
    },
    currentSetIndex: currentSetArrayIndex + 1,
  }
}

const displaySetNumber = (index: number) => {
  const sets = ['1st Set', '2nd Set', '3rd Set']

  return sets[index - 1] ?? `${index}th Set`
}

// =====
// Types
// =====

type PlayerSummary = {
  sets: number
  games: number
  points: number | string
}

type TableScoreSummary = {
  home: PlayerSummary
  away: PlayerSummary
  currentSetIndex?: number
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '--score-table-display-text-color': colors.neutral[800],
  '--score-table-display-score-color': colors.studio[900],
  '--score-table-display-points-background-color': colors.neutral[100],

  '&[data-dark-theme="true"]': {
    '--score-table-display-text-color': colors.studio[200],
    '--score-table-display-score-color': colors.white,
    '--score-table-display-points-background-color': colors.lavender[700],

    '.score-table__divider-wrapper': {
      display: 'initial',
    },
  },

  '.score-table__divider-wrapper': {
    display: 'none',
  },

  width: '100%',

  '> div': {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    columnGap: spacing.smx1,
    rowGap: spacing.smx1,
    alignItems: 'center',
    width: '100%',

    '> div:nth-of-type(1)': {
      fontFamily: font.family.primary,
      fontSize: font.size.sm.fontSize,
      fontWeight: font.weight.regular,
      lineHeight: font.size.sm.lineHeight,
      color: 'var(--score-table-display-text-color)',
    },

    '> div:nth-of-type(2)': {
      display: 'grid',
      gridAutoFlow: 'column',
      gridAutoColumns: '4rem',
      justifyContent: 'end',
      alignItems: 'center',
      columnGap: spacing.smx1,

      '> span': {
        fontFamily: font.family.primary,
        fontSize: font.size.sm.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.sm.lineHeight,
        color: 'var(--score-table-display-text-color)',
        textAlign: 'center',
      },
    },

    '.score-table__divider-wrapper': {
      gridColumn: '1 / -1',
      '> div': {
        borderTop: `1px solid ${colors.studio[600]}`,
      },
    },

    '> div:nth-of-type(4), > div:nth-of-type(6)': {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.smx2,
      minWidth: 0,
      color: 'var(--score-table-display-score-color)',

      '> div': {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.smx2,
        minWidth: 0,

        '& > span:first-of-type': {
          fontFamily: font.family.primary,
          fontSize: font.size.md.fontSize,
          fontWeight: font.weight.regular,
          lineHeight: font.size.md.lineHeight,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },

        '> span + span': {
          width: spacing.smx1,
          height: spacing.smx1,
          borderRadius: radius.full,
          background: colors.warning[500],
          flexShrink: 0,
        },
      },
    },

    '> div:nth-of-type(5), > div:nth-of-type(7)': {
      display: 'grid',
      gridAutoFlow: 'column',
      gridAutoColumns: '4rem',
      justifyContent: 'end',
      alignItems: 'center',
      columnGap: spacing.smx1,

      '> span': {
        fontFamily: font.family.primary,
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        lineHeight: font.size.lg.lineHeight,
        color: 'var(--score-table-display-score-color)',
        textAlign: 'center',
      },

      '> div': {
        width: '3.6rem',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radius.md,
        background: 'var(--score-table-display-points-background-color)',

        '> span': {
          fontFamily: font.family.primary,
          fontSize: font.size.lg.fontSize,
          fontWeight: font.weight.medium,
          lineHeight: font.size.lg.lineHeight,
          color: 'var(--score-table-display-score-color)',
          textAlign: 'center',
        },
      },
    },
  },
})
